import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { Tabs } from '@client/components/_core/navigation/Tabs';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { FinancialDetailsHeader } from '@client/components/claims/financials/FinancialDetailsHeader';
import { ICONS } from '@client/constants/icons';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Typography } from 'antd';

export const Route = createFileRoute('/console/$orgId/claims/details/$id/financials/reserves/details/$reserveId')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Reserve details',
        },
    },
    params: {
        parse({ reserveId }) {
            return {
                reserveId: BigInt(reserveId),
            };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ReserveDetails />
        </PageController>
    );
}

function ReserveDetails() {
    const orgId = useOrgId();
    const { id, reserveId } = Route.useParams();
    const { data } = trpc.reserve.getReserve.useQuery({ id: reserveId });
    const navigate = useNavigate();

    return (
        <OverlayPage
            title={
                <div className="flex items-center gap-2">
                    <ICONS.reserve className="card-title-icon size-5" />
                    <span>Reserve details</span>
                </div>
            }
            size="xLarge"
            afterClose={() => navigate({ moveInMatches: -1 })}
        >
            <FinancialDetailsHeader
                data={{
                    costCode: data?.costCodeFullName,
                    createdAt: data?.createdAt,
                    CreatedBy: {
                        userId: data?.CreatedBy?.userId,
                        photoUrl: data?.CreatedBy?.photoUrl,
                        fullName: data?.CreatedBy?.fullName,
                    },
                }}
                rows={[
                    [
                        {
                            title: 'Total reserve',
                            type: 'currency',
                            children: data?.totalReserveAmount,
                        },
                        {
                            title: 'Total payments',
                            type: 'currency',
                            children: data?.compensationPaymentsAmountSum,
                        },
                        {
                            title: 'Remaining reserve',
                            type: 'currency',
                            children: data?.remainingReserveAmount,
                        },
                    ],
                ]}
            />

            <div className="mb-4 rounded-(--ant-rounded-lg) bg-muted p-4">
                <Typography.Text>{data?.description}</Typography.Text>
            </div>

            <Tabs
                rightAddon={
                    <Button.Add.Link
                        to="/console/$orgId/claims/details/$id/financials/reserves/details/$reserveId/reserve-adjustments/add"
                        params={{
                            orgId,
                            id,
                            reserveId,
                        }}
                        variant="tertiary"
                        label="Add adjustment"
                    />
                }
                primaryActionAddon={
                    <Button.Add.Link
                        to="/console/$orgId/claims/details/$id/financials/compensations/add"
                        params={{
                            orgId,
                            id,
                        }}
                        variant="primary"
                        label="Add payment"
                    />
                }
                items={[
                    {
                        label: 'Movements',
                        to: '/console/$orgId/claims/details/$id/financials/reserves/details/$reserveId/reserve-adjustments',
                    },
                    {
                        label: 'Linked payments',
                        to: '/console/$orgId/claims/details/$id/financials/reserves/details/$reserveId/compensation-payments',
                    },
                    {
                        label: 'Library',
                        to: '/console/$orgId/claims/details/$id/financials/reserves/details/$reserveId/library',
                    },
                ]}
            />
        </OverlayPage>
    );
}
